/*
  Refer a friend page: the "Your link" pop-up, the Recharge lookup, the scroll reveal and the
  small parallax in How it works. Loaded with defer by sections/refer-how-it-works.liquid.

  The pop-up resolves to one of: { status: 'logged_out' } | { status: 'link', link } |
  { status: 'no_link' } | { status: 'error' }. Anyone with a referral link sees it, subscriber or
  one-time buyer. Facts behind the Recharge calls, with sources:
  referral-page/work/sdk-facts.md (Recharge storefront SDK 2.6.1).

  Privacy: the link contains the customer's first name. It is only ever written to an input's
  value (never with innerHTML) and never logged. A link that looks masked or redacted is not
  shown (error content instead).

  When: a logged-in customer's Recharge lookup starts when the browser is idle after the page
  load, or right away when the pop-up opens (also with ?open=1 after login). If the lookup fails
  (network, timeout), the next opening tries once more.

  Preview-only mock: ?rf_state=loading|logged_out|link|no_link|error works in the
  theme editor and on unpublished or development themes, never on the live theme.
*/
(function () {
  'use strict';

  if (window.ReferAFriend) return;

  var SDK_URL = 'https://static.rechargecdn.com/assets/storefront/recharge-client-2.6.1.min.js';
  var TIMEOUT_MS = 12000;
  var IDLE_TIMEOUT_MS = 2500;
  var RETRIES = 1;
  var LINK_HOST = 'rc-refer.com';
  var MOCK_LINK = 'https://rc-refer.com/daniel-a1b2c3';
  var MOCK_DELAY_MS = 900;
  var STATES = ['loading', 'logged_out', 'link', 'no_link', 'error'];
  var LOG_PREFIX = '[refer-a-friend] ';
  /* characters that redaction uses to hide customer data (asterisks, bullets, an ellipsis), and spaces */
  var MASK_CHARS = /[*\u2022\u25CF\u2026\s]/;
  var SLUG_CHARS = (function () {
    try {
      return new RegExp("^[\\p{L}\\p{M}\\p{N}._'-]+$", 'u');
    } catch (e) {
      return /^[A-Za-z0-9._'-]+$/;
    }
  })();

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var warned = {};

  function warnOnce(key, message) {
    if (warned[key]) return;
    warned[key] = true;
    if (window.console && console.warn) console.warn(LOG_PREFIX + message);
  }

  function params() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }
  }

  /* Mock states only off the live theme: theme editor, preview and development themes. */
  function previewAllowed() {
    var shopify = window.Shopify;
    if (!shopify) return false;
    if (shopify.designMode === true) return true;
    return !!(shopify.theme && shopify.theme.role && shopify.theme.role !== 'main');
  }

  function wantedMockState(config) {
    if (!previewAllowed()) return null;
    var p = params();
    var fromUrl = p && p.get('rf_state');
    if (fromUrl && STATES.indexOf(fromUrl) !== -1) return fromUrl;
    var shopify = window.Shopify;
    if (shopify && shopify.designMode === true && config.editorState && STATES.indexOf(config.editorState) !== -1) {
      return config.editorState;
    }
    return null;
  }

  function mockState(state) {
    return new Promise(function (resolve) {
      if (state === 'loading') return; /* stays on the placeholder, to check it */
      setTimeout(function () {
        if (state === 'link') resolve({ status: 'link', link: MOCK_LINK });
        else if (state === 'error') resolve({ status: 'error', retry: true }); /* acts like a failed lookup */
        else resolve({ status: state });
      }, MOCK_DELAY_MS);
    });
  }

  /* ---------- Recharge ---------- */

  var sdkPromise = null;

  function loadSdk() {
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise(function (resolve, reject) {
      var existing = window.recharge;
      if (existing && existing.auth && existing.customer && typeof existing.init === 'function') {
        resolve(existing);
        return;
      }
      var script = document.createElement('script');
      script.src = SDK_URL;
      script.async = true;
      script.onload = function () {
        var sdk = window.recharge;
        if (sdk && sdk.auth && sdk.customer && typeof sdk.init === 'function') resolve(sdk);
        else reject(new Error('sdk-missing'));
      };
      script.onerror = function () {
        reject(new Error('sdk-load'));
      };
      document.head.appendChild(script);
    });
    sdkPromise.catch(function () {
      sdkPromise = null; /* a later attempt may load it */
    });
    return sdkPromise;
  }

  function readLink(customer) {
    var info = customer && customer.include && customer.include.referral_info;
    /* Recharge's example reads customer.referral_info; getCustomer returns it under include. */
    if (!info && customer) info = customer.referral_info;
    var url = info && info.referral_url && info.referral_url.url;
    return typeof url === 'string' && url.trim() ? url.trim() : '';
  }

  /*
    A real link is https://rc-refer.com/<slug>, the slug being <first-name>-<6 random chars>.
    Anything else is not shown: another scheme or host, more than one path part, or masking
    characters (the storefront token's privacy setting can make Recharge redact customer data).
  */
  function isReferralLink(link) {
    if (typeof link !== 'string' || !link || MASK_CHARS.test(link)) return false;
    var parsed;
    try {
      parsed = new URL(link);
    } catch (e) {
      return false;
    }
    if (parsed.protocol !== 'https:' || parsed.hostname !== LINK_HOST) return false;
    if (parsed.port || parsed.username || parsed.password) return false;
    var slug = parsed.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (!slug || slug.indexOf('/') !== -1) return false;
    try {
      slug = decodeURIComponent(slug);
    } catch (e) {
      return false;
    }
    return !MASK_CHARS.test(slug) && SLUG_CHARS.test(slug);
  }

  function hasActiveSubscription(sdk, session, customer) {
    if (customer && typeof customer.subscriptions_active_count === 'number') {
      return Promise.resolve(customer.subscriptions_active_count > 0);
    }
    if (!sdk.subscription || typeof sdk.subscription.listSubscriptions !== 'function') {
      return Promise.reject(new Error('no-active-count'));
    }
    return sdk.subscription.listSubscriptions(session, { status: 'active', limit: 1 }).then(function (response) {
      return !!(response && response.subscriptions && response.subscriptions.length);
    });
  }

  function linkState(link) {
    if (!isReferralLink(link)) {
      warnOnce('link', 'Recharge sent a referral link that looks masked or incomplete, so it is not shown. Check that the storefront token does not redact customer data.');
      return { status: 'error' };
    }
    return { status: 'link', link: link };
  }

  function askRecharge(config) {
    return loadSdk().then(function (sdk) {
      sdk.init({
        storeIdentifier: config.storeIdentifier,
        storefrontAccessToken: config.token,
        appName: 'betterway-refer-a-friend',
        appVersion: '1.0.0',
      });
      return sdk.auth.loginShopifyAppProxy().then(function (session) {
        /* no customerId = this shopper has no Recharge record, so no link yet */
        if (!session || !session.customerId) return { status: 'no_link' };
        return sdk.customer.getCustomer(session, { include: ['referral_info'] }).then(function (customer) {
          var link = readLink(customer);
          if (link) return linkState(link);
          /* Recharge makes the link with an order, so "no link yet"; an active subscriber should already have one */
          return hasActiveSubscription(sdk, session, customer).then(function (active) {
            if (!active) return { status: 'no_link' };
            warnOnce('nolink', 'Recharge has no referral link for this active subscriber. Showing the error message instead.');
            return { status: 'error' };
          });
        });
      });
    });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        reject(new Error('timeout'));
      }, ms);
      promise.then(
        function (value) {
          clearTimeout(timer);
          resolve(value);
        },
        function (error) {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /*
    The one function that decides what the pop-up shows. An error from a failed or slow lookup
    carries retry: true, so the pop-up can ask again; a missing token or bad data does not.
  */
  function getReferralState(config) {
    var mock = wantedMockState(config);
    if (mock) return mockState(mock);
    if (!config.loggedIn) return Promise.resolve({ status: 'logged_out' });
    if (!config.token) {
      warnOnce('token', 'No Recharge storefront token is set in the theme editor, so the referral link cannot load.');
      return Promise.resolve({ status: 'error' });
    }
    return withTimeout(askRecharge(config), TIMEOUT_MS).catch(function (error) {
      var status = error && typeof error.status === 'number' ? ' (HTTP ' + error.status + ')' : '';
      var reason = error && error.message === 'timeout' ? 'Recharge did not answer in time' : 'The Recharge lookup failed';
      warnOnce('lookup', reason + status + '. Showing the error message instead of the link.');
      return { status: 'error', retry: true };
    });
  }

  /* Only a logged-in customer's real lookup goes to the network; the rest answers at once. */
  function needsNetwork(config) {
    return !!(config.loggedIn && config.token) && !wantedMockState(config);
  }

  /* After the page has loaded, when the browser has a moment (so the SDK never competes with the hero). */
  function whenIdle(callback) {
    function later() {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
      else setTimeout(callback, 200);
    }
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  }

  /* ---------- copy ---------- */

  /*
    Fallback when the async Clipboard API is missing or blocked. The temporary textarea goes inside
    the open dialog: showModal() makes the rest of the page inert, and text there cannot be
    selected, so a copy from the body copies nothing (yet execCommand still returns true).
  */
  function legacyCopy(text, host) {
    return new Promise(function (resolve, reject) {
      var parent = host || document.body;
      var before = document.activeElement;
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.setAttribute('tabindex', '-1');
      area.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;margin:0;padding:0;border:0;opacity:0;';
      parent.appendChild(area);
      var ok = false;
      try {
        area.focus({ preventScroll: true });
        area.select();
        area.setSelectionRange(0, text.length);
        ok = document.execCommand('copy');
      } catch (e) {
        ok = false;
      }
      parent.removeChild(area);
      if (before && before !== document.body && before.isConnected && typeof before.focus === 'function') {
        before.focus({ preventScroll: true });
      }
      if (ok) resolve();
      else reject(new Error('copy'));
    });
  }

  function copyText(text, host) {
    var modern =
      navigator.clipboard && window.isSecureContext ? navigator.clipboard.writeText(text) : Promise.reject(new Error('no-clipboard'));
    return modern.catch(function () {
      return legacyCopy(text, host);
    });
  }

  /* ---------- URL helpers ---------- */

  /* Relative URL of this page with open=1, without the preview-only rf_state. */
  function returnPath() {
    var p = params();
    var keep = [];
    if (p) {
      p.forEach(function (value, key) {
        if (key !== 'open' && key !== 'rf_state') keep.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      });
    }
    keep.push('open=1');
    return window.location.pathname + '?' + keep.join('&');
  }

  function dropOpenParam() {
    var p = params();
    if (!p || !p.has('open') || !window.history || !window.history.replaceState) return;
    p.delete('open');
    var query = p.toString();
    try {
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    } catch (e) {
      /* ignore */
    }
  }

  /* ---------- the pop-up ---------- */

  class ReferLinkPopup extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.dialog = this.querySelector('[data-rf-dialog]');
      this.box = this.querySelector('[data-rf-box]');
      if (!this.dialog || !this.box) return;
      this._ready = true;
      this.live = this.querySelector('[data-rf-live]');
      this.openers = Array.prototype.slice.call(this.querySelectorAll('[data-rf-open]'));
      this.lastOpener = this.openers[0] || null;
      this.copyTimer = null;
      this.config = this.readConfig();
      this.result = null; /* the last answer shown */
      this.lookupId = 0;
      this.pending = false;
      this.retriesLeft = RETRIES;
      this.openedByPage = false; /* opened by ?open=1 or the editor preview, not by a click */
      this.pressOnBackdrop = false;

      this.openers.forEach((button) => {
        button.addEventListener('click', () => {
          this.lastOpener = button;
          this.open();
        });
      });
      const closeButton = this.querySelector('[data-rf-close]');
      if (closeButton) closeButton.addEventListener('click', () => this.close());
      /*
        A click on the backdrop closes. The press has to start there too: selecting the link
        with the mouse and letting go outside the box also fires a click on the dialog.
      */
      this.dialog.addEventListener(window.PointerEvent ? 'pointerdown' : 'mousedown', (event) => {
        this.pressOnBackdrop = event.target === this.dialog;
      });
      this.dialog.addEventListener('click', (event) => {
        const onBackdrop = event.target === this.dialog && this.pressOnBackdrop;
        this.pressOnBackdrop = false;
        if (onBackdrop) this.close();
      });
      this.dialog.addEventListener('close', () => {
        dropOpenParam();
        /* after ?open=1 the visitor is still at the top: give focus back without scrolling down */
        const keepScroll = this.openedByPage;
        this.openedByPage = false;
        if (this.lastOpener && this.lastOpener.isConnected) this.lastOpener.focus({ preventScroll: keepScroll });
      });
      const copyButton = this.querySelector('[data-rf-copy]');
      if (copyButton) copyButton.addEventListener('click', () => this.copy(copyButton));
      const login = this.querySelector('[data-rf-login]');
      if (login) {
        /* keep Shopify's login URL and its other parameters, refresh return_to with the current query */
        try {
          const url = new URL(login.getAttribute('href'), window.location.origin);
          url.searchParams.set('return_to', returnPath());
          login.setAttribute('href', url.pathname + url.search);
        } catch (e) {
          /* the Liquid href stays */
        }
      }

      const p = params();
      const shopify = window.Shopify;
      const editorPreview =
        !!(shopify && shopify.designMode === true) && !!this.config.editorState && this.config.editorState !== 'live';
      if ((p && p.has('open')) || editorPreview) {
        this.open({ byPage: true });
      } else if (needsNetwork(this.config)) {
        whenIdle(() => {
          if (this.isConnected && !this.lookupId) this.lookup();
        });
      } else {
        this.lookup(); /* logged out, mock or no token: answers at once, no network */
      }
    }

    disconnectedCallback() {
      clearTimeout(this.copyTimer);
    }

    readConfig() {
      const config = { loggedIn: false, storeIdentifier: '', token: '', editorState: null };
      const node = this.querySelector('script[data-rf-config]');
      if (!node) return config;
      try {
        const parsed = JSON.parse(node.textContent);
        Object.keys(config).forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(parsed, key)) config[key] = parsed[key];
        });
      } catch (e) {
        warnOnce('config', 'The pop-up settings could not be read.');
      }
      config.token = typeof config.token === 'string' ? config.token.trim() : '';
      return config;
    }

    show(name) {
      this.box.querySelectorAll('[data-state]').forEach((panel) => {
        panel.hidden = panel.getAttribute('data-state') !== name;
      });
      this.box.setAttribute('aria-busy', name === 'loading' ? 'true' : 'false');
    }

    render(result) {
      let status = result && STATES.indexOf(result.status) !== -1 ? result.status : 'error';
      this.result = result && status === result.status ? result : { status: 'error' };
      if (status === 'link') {
        if (!isReferralLink(result.link)) {
          status = 'error';
          this.result = { status: 'error' };
        } else {
          const field = this.querySelector('[data-rf-field]');
          if (field) {
            field.value = result.link;
            field.title = result.link;
          }
          const email = this.querySelector('[data-rf-share-email]');
          const text = this.querySelector('[data-rf-share-text]');
          if (email) email.setAttribute('href', 'mailto:?body=' + encodeURIComponent(result.link));
          if (text) text.setAttribute('href', 'sms:?&body=' + encodeURIComponent(result.link));
        }
      }
      this.show(status);
      /* if the pop-up is already open, tell screen readers what replaced the loading sun (WCAG 4.1.3) */
      if (this.dialog.open && this.live) {
        const panel = this.box.querySelector('[data-state="' + status + '"]');
        const line = panel && panel.querySelector('.rf-yl-text');
        const field = panel && panel.querySelector('[data-rf-field]');
        this.live.textContent = line ? line.textContent.trim() : field ? field.getAttribute('aria-label') || '' : '';
      }
    }

    /*
      Ask for the answer and show the loading content meanwhile. Only the latest ask may render,
      so a late answer from an earlier try never replaces a newer one.
    */
    lookup() {
      const id = ++this.lookupId;
      this.pending = true;
      this.show('loading');
      const done = (result) => {
        if (id !== this.lookupId) return;
        this.pending = false;
        if (this.isConnected) this.render(result);
      };
      getReferralState(this.config).then(done, () => done({ status: 'error', retry: true }));
    }

    open(options) {
      if (this.dialog.open) return;
      this.openedByPage = !!(options && options.byPage);
      if (!this.lookupId) {
        this.lookup(); /* opened before the idle-time lookup started */
      } else if (!this.pending && this.result && this.result.retry && this.retriesLeft > 0) {
        this.retriesLeft -= 1; /* the last lookup failed: try once more, behind the loading content */
        this.lookup();
      }
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    close() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
    }

    copy(button) {
      const field = this.querySelector('[data-rf-field]');
      const full = field ? field.value : '';
      if (!full) return;
      if (!button.hasAttribute('data-label-copy')) button.setAttribute('data-label-copy', button.textContent.trim());
      const copied = button.getAttribute('data-label-copied') || 'Copied!';
      copyText(full, this.box).then(
        () => {
          button.textContent = copied;
          if (this.live) this.live.textContent = copied;
          clearTimeout(this.copyTimer);
          this.copyTimer = setTimeout(() => {
            button.textContent = button.getAttribute('data-label-copy');
            if (this.live) this.live.textContent = '';
          }, 2000);
        },
        () => {
          /* copying is blocked: select the text so the visitor can copy it by hand */
          if (field) {
            field.focus();
            field.select();
          }
        }
      );
    }
  }

  /* ---------- scroll reveal (fade + rise 22px), only for elements below the fold ---------- */

  function initReveal(root) {
    var nodes = Array.prototype.slice.call(root.querySelectorAll('.rf-rv:not(.rf-rv--armed)'));
    if (!nodes.length || reduceMotion || !('IntersectionObserver' in window)) return;
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('is-in', entry.isIntersecting);
        });
      },
      { threshold: 0.12 }
    );
    nodes.forEach(function (node) {
      var box = node.getBoundingClientRect();
      if (box.top < viewport && box.bottom > 0) node.classList.add('is-in'); /* already visible: no blink */
      node.classList.add('rf-rv--armed');
      observer.observe(node);
    });
  }

  /* ---------- How it works parallax: --p from -1 to 1 while the section crosses the screen ---------- */

  function initParallax(root) {
    var sections = Array.prototype.slice.call(root.querySelectorAll('[data-rf-parallax]'));
    if (root.matches && root.matches('[data-rf-parallax]')) sections.push(root);
    sections.forEach(function (section) {
      if (section._rfParallax) return;
      section._rfParallax = true;
      if (!('IntersectionObserver' in window)) return;
      var ticking = false;
      var listening = false;
      function update() {
        ticking = false;
        var box = section.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var p = (box.top + box.height / 2 - vh / 2) / ((vh + box.height) / 2);
        section.style.setProperty('--p', Math.max(-1, Math.min(1, p)).toFixed(3));
      }
      function request() {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      }
      new IntersectionObserver(
        function (entries) {
          var visible = entries[entries.length - 1].isIntersecting;
          section.classList.toggle('rf-on-screen', visible);
          if (reduceMotion) return;
          if (visible && !listening) {
            listening = true;
            window.addEventListener('scroll', request, { passive: true });
            window.addEventListener('resize', request);
            update();
          } else if (!visible && listening) {
            listening = false;
            window.removeEventListener('scroll', request);
            window.removeEventListener('resize', request);
          }
        },
        { rootMargin: '120px 0px' }
      ).observe(section);
    });
  }

  function initRoot(root) {
    initReveal(root);
    initParallax(root);
  }

  window.ReferAFriend = { getReferralState: getReferralState };

  if (window.customElements && !window.customElements.get('refer-link-popup')) {
    window.customElements.define('refer-link-popup', ReferLinkPopup);
  }

  initRoot(document);
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initRoot(event.target);
  });
})();
